import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { onLoad, onUnload } from '../actions/pages';
import AppRouter from '../routers/AppRouter';
import LoadingPage from './LoadingPage';
import { isBlocked, pageTitle, roles, userAllowed } from '../helpers/utils';
import { verifyAccount } from '../actions/auth';
import WebsocketClient from '../services/WebsocketClient';
import EnterpriseMismatch from './admin/EnterpriseMismatch';
import { FilterProvider } from '../contexts/FilterContext';
import { ENABLE_PENDO_LOCALLY } from '../helpers/featureFlags';
import { SOLUTION_TYPE } from '../helpers/const';

function App() {
    const {
        signedIn,
        locationId,
        admin,
        enterpriseId,
        enterpriseMismatch,
        isPwnTracker,
        isTrackerComplete,
        solutionType,
        roles: userRoles
    } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const [checkInterval, setCheckInterval] = useState();

    const [isLoading, setLoading] = useState(true);
    const [isEnterpriseMismatch, setEnterpriseMismatch] = useState(false);
    const [connectWebsocket, setWebsocket] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const sessionCheck = () => {
        dispatch(verifyAccount());
    };

    useEffect(() => {
        dispatch(onLoad('APP'));
        document.title = pageTitle();

        // call server and see if user is logged in
        dispatch(verifyAccount('init'));

        // 16 minutes (15 minutes is backend timeout)
        setCheckInterval(setInterval(sessionCheck, 960000));

        const head = document.getElementsByTagName('head')[0];

        // Pendo script for analytics starts
        const pendoScript = document.createElement('script');
        pendoScript.type = 'text/javascript';
        pendoScript.async = true;
        pendoScript.innerHTML = `
            (function(apiKey){
            (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
        v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
        })('230b4cd0-7684-497b-679f-3d712dc4ade5');`;
        if (!location.host.includes('localhost') || ENABLE_PENDO_LOCALLY) head.appendChild(pendoScript);
        // Pendo script for analytics ends

        return () => {
            dispatch(onUnload('APP'));
        };
    }, []);

    /**
     * signedIn = undefined :: when pages loads we dont know signed in status
     * locationId :: plte site location selected
     * admin = true/false :: admin users via forgerock
     * enterpriseId :: enterprise admin user logged into
     * enterpriseMismatch :: enterpriseid in session different then session call enterpriseId
     */
    useEffect(() => {
        const loading =
            signedIn === undefined ||
            (admin === false && locationId === undefined && !isPwnTracker) ||
            (admin === true && enterpriseId && locationId === undefined && !isPwnTracker);
        const connectWebsockets = !!signedIn && !isBlocked(pathname) && (admin === false || (admin === true && enterpriseId && !enterpriseMismatch));
        const mismatch = admin === true && enterpriseMismatch;

        setLoading(loading);
        setWebsocket(connectWebsockets);
        setEnterpriseMismatch(mismatch);
    }, [signedIn, locationId, admin, enterpriseId, pathname, enterpriseMismatch]);

    /**
     * If we detect the session going from one enterpriseId to another enterpriseId without first going to undefined then
     * another browser tab was opened and logged into a different enterprise.  For the safety of the user we will now allow
     * the user to proceed and show an error message, stop session check, disconnect websockets
     */
    useEffect(() => {
        if (enterpriseMismatch) {
            clearInterval(checkInterval);
            setCheckInterval();
        }
    }, [enterpriseMismatch]);

    /**
     * If the enterprise is in Pwn tracker mode, redirect them to the pwn tracker page
     */
    useEffect(() => {
        const isTracker = isPwnTracker && (solutionType === SOLUTION_TYPE.UNKNOWN || !isTrackerComplete);
        const isBetween = isPwnTracker && solutionType !== SOLUTION_TYPE.UNKNOWN;
        const shouldRedirect =
            (isTracker || (isBetween && !userAllowed(userRoles, [roles.VIEW_OSND_URL_IN_TRACKER]))) &&
            pathname !== '/pwn-tracker' &&
            pathname !== '/journey/guide';
        if (shouldRedirect) {
            setIsRedirecting(true);
            window.location.href = '/pwn-tracker';
        } else {
            setIsRedirecting(false);
        }
    }, [isPwnTracker, solutionType, isTrackerComplete, userRoles, pathname]);

    if (isLoading || isRedirecting) {
        return <LoadingPage />;
    }
    return (
        <>
            <FilterProvider>
                <AppRouter />
                {isEnterpriseMismatch && <EnterpriseMismatch />}
                {connectWebsocket && <WebsocketClient />}
            </FilterProvider>
        </>
    );
}
export default App;
